import cn from 'classnames';
import { forwardRef } from 'react';

import Slot from '../../../utils/slot';

import type { ForwardedRef, ReactElement } from 'react';

import type { ActionsContainerProps } from './actions-container.types';

import './actions-container.scss';

/**
 * This component is a container for actions, such as buttons, that should be displayed together.
 * It ensures that spaces between these actions adhere to the design system specifications,
 * providing a consistent look and feel.
 *
 * Additionally, it supports being used as a slot for different components, maintaining appropriate
 * spacing as per the design system guidelines.
 *
 * ### Usage
 *
 * ```tsx
 * import { ActionsContainer } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <ActionsContainer>
 *       <Button type='tertiary'>Cancel</Button>
 *       <Button type='primary'>Save</Button>
 *     </ActionsContainer>
 *   );
 * }
 * ```
 */
const ActionsContainer = forwardRef(
  (props: ActionsContainerProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
    const {
      children,
      className,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    return (
      <Slot name="ActionsContainer">
        <div {...rest} className={cn('rbui-actions-container', className)} ref={forwardedRef}>
          {children}
        </div>
      </Slot>
    );
  },
);

ActionsContainer.displayName = 'ActionsContainer';

export default ActionsContainer;
